// pages/FormsPanel.js
// ---------------------------------------------------------------------------
// SIMPLIFIED Forms feature:
//   - Left (middle column): list of forms — just names, e.g. "Contact Form"
//   - Right: every submission that came in for the selected form, shown as
//     rows of whatever data was submitted (no field configuration needed —
//     the submitted data itself defines what's shown)
//
// Adding a form = typing a name, same as "+ New Page". Nothing else.
// ---------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';

const slugify = s => s.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

function NewFormModal({ onConfirm, onClose }) {
  const [name, setName] = useState('');
  const submit = () => name.trim() && onConfirm(name.trim(), slugify(name.trim()));
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">New Form</div>
        <label className="mlabel">Form Name</label>
        <input className="minput" value={name} onChange={e => setName(e.target.value)}
          placeholder="e.g. Contact Form, Distributor Form..." autoFocus
          onKeyDown={e => e.key === 'Enter' && submit()} />
        <div className="mactions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-solid" onClick={submit}>Create</button>
        </div>
      </div>
    </div>
  );
}

export default function FormsPanel({ API, canAddOrDelete }) {
  const [forms, setForms] = useState([]);
  const [activeForm, setActiveForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [modal, setModal] = useState(null);
  const [loadingSubs, setLoadingSubs] = useState(false);

  useEffect(() => {
    API.get('/api/forms').then(r => setForms(r.data)).catch(() => {});
  }, [API]);

  const openForm = (form) => {
    setActiveForm(form);
    setLoadingSubs(true);
    API.get(`/api/forms/${form.slug}/submissions`)
      .then(r => setSubmissions(r.data))
      .catch(() => setSubmissions([]))
      .finally(() => setLoadingSubs(false));
  };

  const handleCreateForm = async (name, slug) => {
    if (!canAddOrDelete) return;
    try {
      const res = await API.post('/api/forms', { name, slug });
      setForms(p => [...p, res.data]);
      setModal(null);
    } catch (e) { alert('Error creating form: ' + (e?.response?.data?.message || e.message)); }
  };

  const handleDeleteSubmission = async (id) => {
    if (!canAddOrDelete) return;
    if (!window.confirm('Delete this submission?')) return;
    try {
      await API.delete(`/api/forms/${activeForm.slug}/submissions/${id}`);
      setSubmissions(p => p.filter(s => s._id !== id));
    } catch (e) { alert('Error deleting submission'); }
  };

  const downloadCsv = () => {
    if (!activeForm) return;
    const base = API.defaults.baseURL || '';
    window.open(`${base}api/forms/${activeForm.slug}/submissions/download`, '_blank');
  };

  // Union of columns seen across submissions — the data defines the table shape
  const columns = Array.from(
    submissions.reduce((set, s) => { Object.keys(s.data || {}).forEach(k => set.add(k)); return set; }, new Set())
  );

  return (
    <>
      <section className="nav-panel" style={{ width: 260, flexShrink: 0 }}>
        <div className="breadcrumb-bar">
          <div className="bc-placeholder">Forms</div>
          {canAddOrDelete && <button className="icon-btn" onClick={() => setModal('newForm')} title="Add form">+</button>}
        </div>
        <div className="nav-list">
          {forms.length === 0 && <div className="nav-empty-msg">No forms yet.<br/>Click + to add one.</div>}
          {forms.map(f => (
            <div key={f._id} className={`nav-item ${activeForm?._id === f._id ? 'selected' : ''}`} onClick={() => openForm(f)}>
              <span className="nav-item-name">{f.name}</span>
            </div>
          ))}
        </div>
      </section>

      <main className="editor">
        <div className="editor-body">
          {!activeForm && (
            <div className="editor-empty"><div>Select a form, or create one</div></div>
          )}

          {activeForm && (
            <>
              <div className="data-editor-header">
                <div className="data-editor-title"><span className="data-editor-icon">▤</span>{activeForm.name} — Submissions ({submissions.length})</div>
                <button className="btn-add-field" onClick={downloadCsv} disabled={submissions.length === 0}>⬇ Download CSV</button>
              </div>

              {loadingSubs && <div className="no-fields">Loading…</div>}
              {!loadingSubs && submissions.length === 0 && <div className="no-fields">No submissions yet.</div>}

              {!loadingSubs && submissions.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{textAlign:'left',padding:'8px 10px',borderBottom:'1px solid var(--border2)',color:'var(--muted)'}}>Submitted</th>
                        {columns.map(c => (
                          <th key={c} style={{textAlign:'left',padding:'8px 10px',borderBottom:'1px solid var(--border2)',color:'var(--muted)'}}>{c}</th>
                        ))}
                        {canAddOrDelete && <th style={{borderBottom:'1px solid var(--border2)'}}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map(s => (
                        <tr key={s._id}>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid var(--border2)',color:'var(--muted2)',whiteSpace:'nowrap'}}>
                            {new Date(s.submittedAt).toLocaleString()}
                          </td>
                          {columns.map(c => (
                            <td key={c} style={{padding:'8px 10px',borderBottom:'1px solid var(--border2)'}}>{String((s.data || {})[c] ?? '')}</td>
                          ))}
                          {canAddOrDelete && (
                            <td style={{padding:'8px 10px',borderBottom:'1px solid var(--border2)'}}>
                              <button className="rm-btn" onClick={() => handleDeleteSubmission(s._id)}>×</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {modal === 'newForm' && canAddOrDelete && <NewFormModal onConfirm={handleCreateForm} onClose={() => setModal(null)} />}
    </>
  );
}